import { useState, useCallback, useRef } from "react";

/**
 * Hook for resizable table columns via drag handles.
 * Returns column widths and a handler to attach to header cells.
 *
 * Usage:
 *   const { getHeaderProps, getResizeHandle } = useColumnResize({ minWidth: 60 });
 *   <th {...getHeaderProps(colKey)} style={{ width: widths[colKey] }}>
 *     {label}
 *     {getResizeHandle(colKey)}
 *   </th>
 */
export function useColumnResize(opts?: { minWidth?: number }) {
  const minWidth = opts?.minWidth ?? 50;
  const [widths, setWidths] = useState<Record<string, number>>({});
  const dragRef = useRef<{
    key: string;
    startX: number;
    startW: number;
  } | null>(null);

  const onMouseDown = useCallback(
    (key: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const th = (e.target as HTMLElement).closest("th");
      if (!th) return;
      const startW = th.getBoundingClientRect().width;
      dragRef.current = { key, startX: e.clientX, startW };

      const onMouseMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        const delta = ev.clientX - dragRef.current.startX;
        const newW = Math.max(minWidth, dragRef.current.startW + delta);
        setWidths((prev) => ({ ...prev, [dragRef.current!.key]: newW }));
      };

      const onMouseUp = () => {
        dragRef.current = null;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [minWidth]
  );

  const getResizeHandle = useCallback(
    (key: string) => (
      <span
        onMouseDown={(e) => onMouseDown(key, e)}
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-primary/40 active:bg-primary/60"
        style={{ zIndex: 40 }}
      />
    ),
    [onMouseDown]
  );

  return { widths, getResizeHandle };
}
